import * as React from 'react';
import { Shield, ShieldCheck, Lock, Bell, FileSearch, RefreshCw, Ban, Eye, Wrench, Users, BadgeCheck, Radar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { CurrencySelector } from '@/components/portal/CurrencySelector';
import { useCurrency } from '@/hooks/use-currency';

export type TierType = 'essential' | 'premium';

interface TierSelectionProps {
  onSelect: (tier: TierType) => void;
  selectedTier?: TierType;
}

interface TierConfig {
  name: string;
  price: number;
  description: string;
  note?: string;
  icon: typeof Shield;
  topBadge?: string;
  features: { icon: typeof Shield; label: string; highlight?: boolean }[];
}

type BulkTier = 'retail' | '1000' | '2000' | '5000';

const getBulkTier = (siteCount: number): BulkTier => {
  if (siteCount >= 5000) return '5000';
  if (siteCount >= 2000) return '2000';
  if (siteCount >= 1000) return '1000';
  return 'retail';
};

export const getBulkEstimatePricePerSite = (tier: TierType, siteCount: number) => {
  const bulkTier = getBulkTier(siteCount);
  if (tier === 'essential') {
    if (bulkTier === '5000') return 25;
    if (bulkTier === '2000') return 30;
    if (bulkTier === '1000') return 40;
    return 50;
  }

  if (bulkTier === '5000') return 80;
  if (bulkTier === '2000') return 85;
  if (bulkTier === '1000') return 90;
  return 185;
};

const TIERS: Record<TierType, TierConfig> = {
  essential: {
    name: 'Essential',
    price: 50,
    description: 'Managed WordPress Security',
    note: 'Strong automatic protection with human oversight and vulnerability intelligence.',
    icon: Shield,
    topBadge: 'Essential',
    features: [
      { icon: Shield, label: 'Detects and contains repeated login abuse automatically' },
      { icon: Radar, label: 'Monitors integrity changes and admin activity' },
      { icon: Eye, label: 'Early visibility into newly disclosed vulnerabilities' },
      { icon: FileSearch, label: 'Prioritises vulnerabilities by exploitability and exposure' },
      { icon: Ban, label: 'Reduces exposed entry points like XML-RPC and enumeration' },
      { icon: Lock, label: 'Secure login controls including 2FA and access hardening' },
      { icon: Users, label: 'User enumeration and access-abuse reduction' },
      { icon: FileSearch, label: 'File integrity monitoring for unauthorised changes' },
      { icon: Bell, label: 'Validated alerts to reduce false positives' },
      { icon: RefreshCw, label: 'Guides safe core, plugin, and theme updates' },
      { icon: FileSearch, label: 'Clear remediation steps and runbooks' },
      { icon: Wrench, label: 'Baseline hardening recommendations' },
      { icon: BadgeCheck, label: 'No setup fees or routine labour charges' },
    ],
  },
  premium: {
    name: 'Premium',
    price: 185,
    description: 'SOC Operations and Incident Ownership',
    note: 'Human-led WordPress security with analyst oversight and remediation.',
    icon: ShieldCheck,
    topBadge: 'For Enterprise',
    features: [
      { icon: ShieldCheck, label: 'Includes everything in Essential', highlight: true },
      { icon: Users, label: 'Active monitoring by security analysts', highlight: true },
      { icon: Eye, label: 'Incident ownership with containment and stabilization', highlight: true },
      { icon: Shield, label: 'Remediation performed by ShadowScan', highlight: true },
      { icon: Bell, label: 'Priority response coordination', highlight: true },
      { icon: Wrench, label: 'Post-incident review and hardening', highlight: true },
      { icon: RefreshCw, label: 'Continuous tuning of thresholds and controls', highlight: true },
      { icon: Radar, label: 'Ongoing optimisation based on live signals', highlight: true },
      { icon: Users, label: 'Dedicated account manager (for Premium portfolios)', highlight: true },
    ],
  },
};

export function TierSelection({ onSelect, selectedTier }: TierSelectionProps) {
  const [siteCount, setSiteCount] = React.useState<number>(1);
  const { formatCurrency } = useCurrency();

  const bulkTier = getBulkTier(siteCount);
  const bulkLabel =
    bulkTier === '1000' ? '1,000+ sites' : bulkTier === '2000' ? '2,000+ sites' : bulkTier === '5000' ? '5,000+ sites' : null;
  return (
    <div className="space-y-6">
      <Card className="cyber-card">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg">Bulk pricing estimator</CardTitle>
              <p className="text-sm text-muted-foreground">Adjust estimated portfolio size to reflect bulk pricing.</p>
            </div>
            <CurrencySelector />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Portfolio size: <span className="text-foreground font-medium">{siteCount.toLocaleString('en-AU')}</span>
            </div>
            {bulkLabel ? <Badge variant="outline">{bulkLabel}</Badge> : null}
          </div>
          <Slider
            value={[siteCount]}
            min={1}
            max={5000}
            step={1}
            onValueChange={(v) => setSiteCount(v[0] ?? 1)}
          />
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
      {(Object.keys(TIERS) as TierType[]).map((tierKey) => {
        const tier = TIERS[tierKey];
        const isSelected = selectedTier === tierKey;
        const TierIcon = tier.icon;
        const bulkPrice = getBulkEstimatePricePerSite(tierKey, siteCount);
        const estimatedLabel = "Estimated conversion; billed in AUD.";

        return (
          <Card
            key={tierKey}
            className={`cyber-card cursor-pointer transition-all duration-200 hover:border-primary/50 ${
              isSelected ? 'ring-2 ring-primary border-primary' : ''
            } ${tier.topBadge ? 'relative' : ''}`}
            onClick={() => onSelect(tierKey)}
          >
            {tier.topBadge && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                {tier.topBadge}
              </Badge>
            )}
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${tierKey === 'premium' ? 'bg-primary/20' : 'bg-muted'}`}>
                    <TierIcon className={`h-6 w-6 ${tierKey === 'premium' ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-4xl font-mono font-bold" title={estimatedLabel}>{formatCurrency(bulkPrice)}</span>
                <span className="text-muted-foreground"> / site / month</span>
              </div>
              {tier.note && <p className="mt-3 text-sm text-muted-foreground">{tier.note}</p>}
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {tier.features.map((feature, idx) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <li
                      key={idx}
                      className={`flex items-center gap-2 text-sm ${
                        feature.highlight ? 'text-primary font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      <FeatureIcon className={`h-4 w-4 flex-shrink-0 ${feature.highlight ? 'text-primary' : ''}`} />
                      {feature.label}
                      {feature.highlight && (
                        <Badge variant="outline" className="ml-auto text-xs border-primary/50 text-primary">
                          Premium
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
              <Button
                className={`w-full mt-6 ${isSelected ? '' : 'variant-outline'}`}
                variant={isSelected ? 'default' : 'outline'}
              >
                {isSelected ? 'Selected' : `Select ${tier.name}`}
              </Button>
            </CardContent>
          </Card>
        );
      })}
      </div>
    </div>
  );
}

export const TIER_PRICES = {
  essential: {
    price_id: 'price_1Shmkw53YIqi52EpPzMyMKmn',
    price_per_domain: 50,
  },
  premium: {
    price_id: 'price_1SbFT353YIqi52Epb9WL8laA',
    price_per_domain: 185,
  },
};

export const CANCELLATION_FEES: Record<TierType, number> = {
  essential: 45,
  premium: 99,
};
